/**
 * The fixed balance table. This module is the *only* place where an
 * interpretation turns into numbers. The AI never touches these values; it can
 * only choose which effect modules appear and how intense they should feel.
 *
 * Everything here is pure and deterministic: same input -> same output. That
 * property is what makes the ruleset portable to another engine and testable.
 */
import {
  BALANCE_RULES_VERSION,
  clamp,
  type EffectModule,
} from '@spell/shared-types';
import {
  chantSyllables,
  chantWordCount,
  type EffectSpec,
  type SpellInterpretation,
  type SpellStats,
} from '@spell/spell-schema';

export { BALANCE_RULES_VERSION };

/** Per-module budget weight and the stat contributions it unlocks. */
interface ModuleRule {
  /** Budget points consumed at intensity 1.0. */
  weight: number;
  /** Minimum share of the weight consumed even at intensity 0. */
  floor: number;
  /** Modules that only make sense on a parry spell. */
  parryOnly?: boolean;
  /** Modules that make the spell impossible for the opponent to parry. */
  unparryable?: boolean;
}

export const MODULE_RULES: Record<EffectModule, ModuleRule> = {
  Projectile: { weight: 34, floor: 0.55 },
  Homing: { weight: 30, floor: 0.4 },
  Explosion: { weight: 32, floor: 0.4 },
  Area: { weight: 30, floor: 0.4 },
  DamageOverTime: { weight: 26, floor: 0.35 },
  Heal: { weight: 38, floor: 0.4 },
  Shield: { weight: 30, floor: 0.4 },
  Reflect: { weight: 34, floor: 0.35, parryOnly: true },
  Absorb: { weight: 22, floor: 0.35, parryOnly: true },
  StoreAttack: { weight: 30, floor: 0.4, parryOnly: true },
  Teleport: { weight: 24, floor: 0.5, unparryable: true },
  Dash: { weight: 18, floor: 0.5, unparryable: true },
  Trap: { weight: 28, floor: 0.4 },
  Stun: { weight: 40, floor: 0.35 },
  Slow: { weight: 22, floor: 0.35 },
  Knockback: { weight: 14, floor: 0.4 },
  BuffNextSpell: { weight: 26, floor: 0.4, unparryable: true },
  RemoveProjectile: { weight: 18, floor: 0.5, parryOnly: true },
};

export const BUDGET = {
  /** Baseline effect budget for a one-word signature spell. */
  SIGNATURE_BASE: 96,
  /** Parry spells get a smaller side-effect budget; the block itself is free. */
  PARRY_BASE: 72,
  /** Budget granted per syllable beyond the first. */
  PER_SYLLABLE: 11,
  /** Hard ceiling on the chant-length bonus. */
  MAX_CHANT_BONUS: 66,
} as const;

/**
 * Volleys — many small shots instead of one big one.
 *
 * A volley is not a separate kind of spell and there is no dial for it in the
 * forge. It is what the rules answer with when a player *describes* one, and
 * these are the terms of that answer.
 *
 * The trade it encodes: every shot is separately parryable, but a parry is a
 * whole incantation — hold, chant, sigil, release — so nobody blocks four shots
 * 350 ms apart. A volley therefore gets damage past a defender who would have
 * blocked a single shot outright, and pays for that by carrying less damage in
 * total. Strong against a good parrier, weaker against a bad one, which is
 * exactly the shape a second option should have.
 */
export const VOLLEY = {
  MIN_SHOTS: 2,
  MAX_SHOTS: 4,
  /**
   * Gap between shots.
   *
   * Shorter than the parry window, which means two shots' windows overlap —
   * that is fine and deliberate. `resolveParryWindow` credits a parry to the
   * candidate whose impact is *nearest* the attempt, so an overlap resolves to
   * "you blocked the one about to hit you", which is what the player meant.
   * What the gap has to be long enough for is the eye: shots this far apart
   * read as separate objects arriving in sequence rather than one smear.
   */
  INTERVAL_MS: 350,
  /** Total damage a volley carries, relative to the same budget in one shot. */
  DAMAGE_SCALE: 0.7,
} as const;

/**
 * How many shots a volley is worth.
 *
 * Inverse to intensity: a heavy projectile splits into a couple of substantial
 * pieces, a light one sprays. Never chosen by the interpreter — it only says
 * *that* the player wanted a volley.
 */
export function volleyShots(projectileIntensity: number): number {
  const span = VOLLEY.MAX_SHOTS - VOLLEY.MIN_SHOTS;
  const raw = VOLLEY.MAX_SHOTS - span * clamp(projectileIntensity, 0, 1);
  return clamp(Math.round(raw), VOLLEY.MIN_SHOTS, VOLLEY.MAX_SHOTS);
}

export const COST = {
  SIGNATURE_BASE_MANA: 16,
  PARRY_BASE_MANA: 12,
  MANA_PER_WEIGHT: 0.34,
  SIGNATURE_BASE_CD: 2600,
  PARRY_BASE_CD: 1500,
  CD_PER_WEIGHT: 34,
  /** Cast time: the wand animation + circle build-up, not the chant itself. */
  CAST_BASE_MS: 220,
  CAST_PER_SYLLABLE_MS: 130,
  CAST_MAX_MS: 1500,
} as const;

/**
 * Short chants are allowed to be strong, they just cost more. Long chants leave
 * you exposed while you speak, so they get a small discount on top of the extra
 * budget their syllables already bought.
 */
export function shortChantPenalty(syllables: number): number {
  if (syllables <= 1) return 1.42;
  if (syllables === 2) return 1.22;
  if (syllables === 3) return 1.08;
  if (syllables <= 5) return 1.0;
  return 0.93;
}

export function chantBudgetBonus(syllables: number): number {
  return clamp((syllables - 1) * BUDGET.PER_SYLLABLE, 0, BUDGET.MAX_CHANT_BONUS);
}

/**
 * Everything a chant decides on its own, before anybody knows what the spell
 * does.
 *
 * The forge shows this live while the player is still assembling words, which
 * is the only moment chant length is still a choice. It lives here rather than
 * in the UI because `computeBalance` reads the same values — a forge that
 * predicted a cast time the rules then contradicted would be worse than
 * showing nothing.
 */
export interface ChantEstimate {
  syllables: number;
  words: number;
  /** Effect budget before the chant-length bonus. */
  budgetBase: number;
  budgetBonus: number;
  budgetTotal: number;
  /** True once `MAX_CHANT_BONUS` is reached and more syllables buy nothing. */
  bonusCapped: boolean;
  /** Multiplier applied to mana and cooldown for a short chant. */
  costMultiplier: number;
  /** Wand animation and circle build-up. The chant itself takes as long as it takes. */
  castTimeMs: number;
}

export function estimateChant(
  type: 'parry' | 'signature' | 'basic',
  normalizedChant: string,
): ChantEstimate {
  const syllables = Math.max(1, chantSyllables(normalizedChant));
  const words = Math.max(1, chantWordCount(normalizedChant));
  const budgetBase = type === 'parry' ? BUDGET.PARRY_BASE : BUDGET.SIGNATURE_BASE;
  const budgetBonus = chantBudgetBonus(syllables);
  return {
    syllables,
    words,
    budgetBase,
    budgetBonus,
    budgetTotal: budgetBase + budgetBonus,
    bonusCapped: budgetBonus >= BUDGET.MAX_CHANT_BONUS,
    costMultiplier: shortChantPenalty(syllables),
    castTimeMs: Math.min(
      COST.CAST_MAX_MS,
      COST.CAST_BASE_MS + (syllables - 1) * COST.CAST_PER_SYLLABLE_MS,
    ),
  };
}

function effectiveWeight(spec: EffectSpec): number {
  const rule = MODULE_RULES[spec.module];
  const intensity = clamp(spec.intensity, 0, 1);
  return rule.weight * (rule.floor + (1 - rule.floor) * intensity);
}

/** Deduplicate modules, keeping the strongest intensity of each. */
export function normalizeEffects(effects: EffectSpec[]): EffectSpec[] {
  const best = new Map<EffectModule, number>();
  for (const e of effects) {
    const prev = best.get(e.module) ?? -1;
    if (e.intensity > prev) best.set(e.module, clamp(e.intensity, 0, 1));
  }
  return [...best.entries()].map(([module, intensity]) => ({ module, intensity }));
}

export interface BalanceInput {
  type: 'parry' | 'signature' | 'basic';
  normalizedChant: string;
  effects: EffectSpec[];
  unconditional: boolean;
  /** The player asked for many small shots rather than one big one. */
  volley?: boolean;
}

export interface BalanceResult {
  effects: EffectSpec[];
  stats: SpellStats;
  /** Human-readable notes about how the budget was applied. */
  notes: string[];
}

/**
 * Turn an interpretation into concrete, enforceable numbers.
 *
 * The shape of the algorithm:
 *  1. price the requested modules,
 *  2. compare against a budget derived only from chant length + spell type,
 *  3. scale potency down (never reject) when over budget,
 *  4. price mana and cooldown from the *requested* weight, so an over-ambitious
 *     spell is both weaker and more expensive.
 */
export function computeBalance(input: BalanceInput): BalanceResult {
  const notes: string[] = [];
  const estimate = estimateChant(input.type, input.normalizedChant);
  const { syllables, words } = estimate;

  let effects = normalizeEffects(input.effects);

  // "Blocks absolutely everything" style requests: honour the wish, remove the
  // freebies, and make it expensive enough to be a real decision.
  let unconditionalTax = 1;
  if (input.unconditional && input.type === 'parry') {
    effects = effects.filter((e) => e.module === 'RemoveProjectile');
    if (effects.length === 0) effects = [{ module: 'RemoveProjectile', intensity: 1 }];
    unconditionalTax = 2.35;
    notes.push('unconditional-parry: side effects removed, cost inflated');
  }

  // Parry-only modules on a signature spell (and vice versa) are dropped rather
  // than allowed to silently do nothing.
  const before = effects.length;
  if (input.type === 'signature') {
    effects = effects.filter((e) => !MODULE_RULES[e.module].parryOnly);
    if (effects.length === 0) effects = [{ module: 'Projectile', intensity: 0.5 }];
  }
  if (effects.length !== before) notes.push('dropped modules invalid for this spell type');

  const requestedWeight = effects.reduce((sum, e) => sum + effectiveWeight(e), 0);
  const budgetTotal = estimate.budgetTotal;
  const over = Math.max(0, requestedWeight - budgetTotal);
  const potencyScale = budgetTotal / (budgetTotal + over);
  if (over > 0) {
    notes.push(
      `over budget by ${over.toFixed(0)} — potency scaled to ${(potencyScale * 100).toFixed(0)}%`,
    );
  }

  const penalty = estimate.costMultiplier * unconditionalTax;
  const baseMana = input.type === 'parry' ? COST.PARRY_BASE_MANA : COST.SIGNATURE_BASE_MANA;
  const baseCd = input.type === 'parry' ? COST.PARRY_BASE_CD : COST.SIGNATURE_BASE_CD;

  const manaCost = Math.round((baseMana + requestedWeight * COST.MANA_PER_WEIGHT) * penalty);
  const cooldownMs =
    Math.round(((baseCd + requestedWeight * COST.CD_PER_WEIGHT) * penalty) / 50) * 50;
  const castTimeMs = estimate.castTimeMs;

  const has = (m: EffectModule) => effects.find((e) => e.module === m);
  const inten = (m: EffectModule) => has(m)?.intensity ?? 0;
  const p = potencyScale;

  // --- damage ------------------------------------------------------------
  let damage = 0;
  if (has('Projectile')) damage += (9 + 15 * inten('Projectile')) * p;
  if (has('Explosion')) damage += (7 + 16 * inten('Explosion')) * p;
  if (has('Area')) damage += (4 + 9 * inten('Area')) * p;
  if (has('Trap')) damage += (6 + 12 * inten('Trap')) * p;
  if (has('Homing') && !has('Projectile')) damage += (6 + 10 * inten('Homing')) * p;

  const dotDps = has('DamageOverTime') ? (3 + 6 * inten('DamageOverTime')) * p : 0;
  const dotDurationMs = has('DamageOverTime')
    ? Math.round((1800 + 2600 * inten('DamageOverTime')) * p)
    : 0;

  // --- support -----------------------------------------------------------
  const healAmount = has('Heal') ? Math.round((8 + 20 * inten('Heal')) * p) : 0;
  const shieldAmount = has('Shield') ? Math.round((10 + 24 * inten('Shield')) * p) : 0;
  const reflectRatio = has('Reflect') ? clamp((0.25 + 0.55 * inten('Reflect')) * p, 0, 1) : 0;
  const absorbRatio = has('Absorb') ? clamp((0.3 + 0.6 * inten('Absorb')) * p, 0, 1) : 0;

  // --- control -----------------------------------------------------------
  const stunMs = has('Stun') ? Math.round((450 + 900 * inten('Stun')) * p) : 0;
  const slowMs = has('Slow') ? Math.round((1200 + 2200 * inten('Slow')) * p) : 0;
  const slowFactor = has('Slow') ? clamp(0.25 + 0.4 * inten('Slow') * p, 0, 0.7) : 0;
  const knockback = has('Knockback') ? (1.2 + 2.6 * inten('Knockback')) * p : 0;
  const nextSpellMultiplier = has('BuffNextSpell')
    ? 1 + (0.18 + 0.42 * inten('BuffNextSpell')) * p
    : 1;

  // --- shape -------------------------------------------------------------
  const radius = has('Explosion')
    ? 1.8 + 2.6 * inten('Explosion') * p
    : has('Area')
      ? 2.4 + 3.2 * inten('Area') * p
      : has('Trap')
        ? 1.6 + 2.0 * inten('Trap') * p
        : 0.55 + 0.5 * inten('Projectile');

  // --- volley --------------------------------------------------------------
  // Only something actually delivered by a projectile can be split into
  // several. A trap, an area effect or a self-buff has nothing to divide.
  const canVolley = input.volley === true && input.type !== 'parry' && Boolean(has('Projectile'));
  const projectileCount = canVolley ? volleyShots(inten('Projectile')) : 1;
  const burstIntervalMs = projectileCount > 1 ? VOLLEY.INTERVAL_MS : 0;
  if (projectileCount > 1) {
    // The budget bought a total, and the total is what shrinks — mana and
    // cooldown were priced from the requested weight and do not move. Damage
    // below is the figure for *one* shot, so the simulation can stay ignorant
    // of volleys and just fire what it is told, `projectileCount` times.
    damage = (damage * VOLLEY.DAMAGE_SCALE) / projectileCount;
    notes.push(
      `volley: ${projectileCount} shots ${VOLLEY.INTERVAL_MS}ms apart, ` +
        `total damage ×${VOLLEY.DAMAGE_SCALE} (each shot is parried separately)`,
    );
  }

  const homing = has('Homing') ? clamp(0.35 + 0.55 * inten('Homing'), 0, 1) : 0;
  // Homing projectiles travel slower — that is the counterplay window.
  const speed = clamp(21 - homing * 8 - (has('Explosion') ? 2.5 : 0), 8, 26);

  const durationMs = has('Trap')
    ? Math.round(5000 + 5000 * inten('Trap'))
    : has('Shield')
      ? Math.round(4000 + 4000 * inten('Shield'))
      : Math.max(dotDurationMs, slowMs, stunMs);

  // Escape/utility effects cannot be parried: there is nothing in flight.
  const anyUnparryable = effects.every((e) => MODULE_RULES[e.module].unparryable);
  const parryable = input.type !== 'parry' && !anyUnparryable;

  const stats: SpellStats = {
    damage: round1(damage),
    dotDps: round1(dotDps),
    dotDurationMs,
    healAmount,
    shieldAmount,
    reflectRatio: round2(reflectRatio),
    absorbRatio: round2(absorbRatio),
    stunMs,
    slowMs,
    slowFactor: round2(slowFactor),
    knockback: round2(knockback),
    nextSpellMultiplier: round2(nextSpellMultiplier),
    radius: round2(radius),
    speed: round2(speed),
    projectileCount,
    burstIntervalMs,
    durationMs,
    manaCost,
    cooldownMs,
    castTimeMs,
    parryable,
    homing: round2(homing),
    budgetUsed: round1(requestedWeight),
    budgetTotal: round1(budgetTotal),
    potencyScale: round2(potencyScale),
  };

  notes.push(`${syllables} syllable(s) / ${words} word(s) → penalty ×${penalty.toFixed(2)}`);
  return { effects, stats, notes };
}

export function balanceFromInterpretation(
  type: 'parry' | 'signature',
  normalizedChant: string,
  interpretation: SpellInterpretation,
): BalanceResult {
  return computeBalance({
    type,
    normalizedChant,
    effects: interpretation.effects,
    unconditional: interpretation.unconditional,
    volley: interpretation.volley,
  });
}

const round1 = (v: number) => Math.round(v * 10) / 10;
const round2 = (v: number) => Math.round(v * 100) / 100;
